import { SearchParam } from "../helpers/SearchParam";
import { LanguageKeyFromCode, OneOfTranslations, translationMap } from "../multigame/Translator";
import { Settings } from "../multigame/Settings";

export interface FrontendLaunchSettings {
	/** ISO language code: en, es, pt, ro (also en-US, pt-BR, ...) */
	languageCode: string;
	/** When false, loader does not show Dixpa logo (unbranded). */
	showLogo: boolean;
	/** Branding profile name, e.g. dixpa | unbranded */
	branding: string;
}

/** Bundled defaults used when remote JSON is missing. Keep in sync with FrontendLaunchSettings.json */
const DEFAULT_SETTINGS: FrontendLaunchSettings = {
	languageCode: "en",
	showLogo: false,
	branding: "unbranded",
};

let cachedSettings: FrontendLaunchSettings | null = null;
let loadPromise: Promise<FrontendLaunchSettings> | null = null;

function NormalizeSettings(raw: Partial<FrontendLaunchSettings> | null | undefined): FrontendLaunchSettings {
	const merged = {
		...DEFAULT_SETTINGS,
		...(raw ?? {}),
	};

	const branding = (merged.branding ?? "dixpa").toString().trim().toLowerCase() || "dixpa";
	const languageCode = (merged.languageCode ?? "en").toString().trim() || "en";
	let showLogo = typeof merged.showLogo === "boolean" ? merged.showLogo : branding !== "unbranded";

	// unbranded always hides logo
	if (branding === "unbranded") {
		showLogo = false;
	}

	return {
		languageCode,
		showLogo,
		branding,
	};
}

function SettingsUrl(): string {
	const customUrl = SearchParam("launchSettingsUrl");
	if (customUrl) {
		return customUrl;
	}

	return `${Settings.ResourcesFolder}singlegame/FrontendLaunchSettings.json`;
}

/** Loads launch settings once (language, logo, branding). Falls back to bundled defaults. */
export async function LoadFrontendLaunchSettings(): Promise<FrontendLaunchSettings> {
	if (cachedSettings) {
		return cachedSettings;
	}

	if (loadPromise) {
		return loadPromise;
	}

	loadPromise = (async () => {
		try {
			const response = await fetch(SettingsUrl(), { cache: "no-cache" });
			if (response.ok) {
				const json = await response.json() as Partial<FrontendLaunchSettings>;
				cachedSettings = NormalizeSettings(json);
				return cachedSettings;
			}
		}
		catch {
			// use bundled defaults
		}

		cachedSettings = NormalizeSettings(DEFAULT_SETTINGS);
		return cachedSettings;
	})();

	return loadPromise;
}

export function GetFrontendLaunchSettings(): FrontendLaunchSettings {
	return cachedSettings ?? NormalizeSettings(DEFAULT_SETTINGS);
}

/** Language key (english/spanish/portuguese/...) from launch settings. */
export function GetLaunchLanguageKey(): OneOfTranslations {
	return LanguageKeyFromCode(GetFrontendLaunchSettings().languageCode) ?? "english";
}

export function GetLaunchLanguageLocality(): string {
	const key = GetLaunchLanguageKey();
	return translationMap[key]?.locality ?? translationMap.english.locality;
}

export function ShouldShowLaunchLogo(): boolean {
	return GetFrontendLaunchSettings().showLogo;
}
